import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import {
  PvReception,
  Sav,
  type MondayProjectHit,
  type PoseurTaskType,
  type PvReceptionCreatePayload,
  type Task,
} from "@relais/contracts";
import { useSession } from "../lib/auth.js";
import { useOffline } from "../lib/offline-context.js";
import { apiFetch } from "../lib/api.js";
import { MondaySearch } from "../features/nouvelle-reception/MondaySearch.js";
import { FormTypePicker } from "../features/nouvelle-reception/FormTypePicker.js";
import { splitClientName } from "../features/nouvelle-reception/split-client-name.js";

export const Route = createFileRoute("/nouvelle")({
  component: NouvelleReception,
});

type WizardStep = "search" | "type";

function NouvelleReception() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const { connectionState, service, pushToast } = useOffline();
  const [step, setStep] = useState<WizardStep>("search");
  const [project, setProject] = useState<MondayProjectHit | null>(null);
  const [creating, setCreating] = useState(false);

  if (session.status === "loading") return <p>{t("common.loading")}</p>;
  if (session.status === "anon") return <Navigate to="/login" />;

  // Creation touches the network (Monday search) — everything after task
  // creation (the wizard itself) is fully offline. See task brief.
  if (connectionState !== "online") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-10 text-center">
        <h1 className="text-lg font-semibold">{t("nouvelle.offlineRequired.title")}</h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          {t("nouvelle.offlineRequired.body")}
        </p>
      </div>
    );
  }

  async function createReception(type: PoseurTaskType) {
    if (!project || !service) return;
    setCreating(true);
    try {
      const { first, last } = splitClientName(project.client_name);
      const payload: PvReceptionCreatePayload = {
        type,
        monday_id: project.monday_id,
        name: project.project_name,
        ...(project.project_address ? { address: project.project_address } : {}),
        ...(first ? { client_first_name: first } : {}),
        ...(last ? { client_last_name: last } : {}),
      };
      const task = await apiFetch<Task>("/tasks/pv-reception", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      // Written locally right away so the /pv/$taskId route doesn't have to
      // wait on the background sync loop to know this task exists.
      await service.upsertTasks([task]);
      service.syncNow().catch(() => undefined);
      const firstStep =
        type === "sav" ? Sav.SAV_STEPS[0]!.key : PvReception.PV_RECEPTION_STEPS[0]!.key;
      void navigate({
        to: "/pv/$taskId/$step",
        params: { taskId: task.id, step: firstStep },
      });
    } catch {
      pushToast({ kind: "error", message: t("nouvelle.error.createFailed") });
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        {step === "type" ? (
          <button
            type="button"
            onClick={() => setStep("search")}
            aria-label={t("nouvelle.back")}
            className="inline-flex h-9 w-9 -ml-2 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
        ) : null}
        <h1 className="text-xl font-semibold">{t("nouvelle.title")}</h1>
      </header>

      {step === "search" ? (
        <MondaySearch
          project={project}
          onSelect={setProject}
          onClear={() => setProject(null)}
          onNext={() => setStep("type")}
        />
      ) : (
        <FormTypePicker creating={creating} onCreate={(type) => void createReception(type)} />
      )}
    </div>
  );
}
